// AriaVisual.jsx — WHAT SHE PUTS ON SCREEN (Braeden 8/14)
//
//   "if shes talking about something shes pulling up a visual to show them what
//    shes talking about, very important for communication"
//
// Renders the spec from ariaVisualDecide. Five shapes, each earning its place:
//   table    the actual rows — invoices, jobs. Never a summary of rows.
//   stats    four numbers that answer "how are we doing"
//   toggles  what is on and what is not, with the reason it is not
//   bars     a comparison you cannot feel from a sentence
//   steps    a sequence over time
//
// Theme-aware, no external libraries, and it renders nothing at all when there
// is nothing worth showing — a visual on every turn is wallpaper.
import React from 'react';

const S = `
.avz { margin:10px 0 4px; border-radius:16px; overflow:hidden;
  background:var(--avz-card); border:1px solid var(--avz-line); max-width:640px;
  --avz-card:rgba(255,255,255,.045); --avz-line:rgba(255,255,255,.10);
  --avz-ink:#eaf0f8; --avz-mut:rgba(255,255,255,.56); --avz-acc:#4d8dff; --avz-out:#ff8a5b; --avz-on:#30d158; }
.avz.is-light { --avz-card:#fff; --avz-line:rgba(14,20,32,.12); --avz-ink:#0e1420;
  --avz-mut:rgba(14,20,32,.58); --avz-acc:#0a6bd4; --avz-out:#c2410c; --avz-on:#1a7f37; }
.avz__head { padding:11px 15px; border-bottom:1px solid var(--avz-line);
  font-size:11px; font-weight:800; letter-spacing:.13em; text-transform:uppercase; color:var(--avz-acc); }
.avz__foot { padding:9px 15px; border-top:1px solid var(--avz-line);
  font-size:12px; font-weight:700; color:var(--avz-mut); }
.avz table { width:100%; border-collapse:collapse; }
.avz th { text-align:left; font-size:10px; font-weight:800; letter-spacing:.08em; text-transform:uppercase;
  color:var(--avz-mut); padding:9px 15px; border-bottom:1px solid var(--avz-line); }
.avz td { padding:10px 15px; font-size:13.5px; color:var(--avz-ink); border-bottom:1px solid var(--avz-line); }
.avz tr:last-child td { border-bottom:0; }
.avz td:last-child { text-align:right; color:var(--avz-mut); font-weight:700; font-size:12px; }
.avz__empty { padding:18px 15px; font-size:13px; color:var(--avz-mut); }

.avz__stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(110px,1fr)); }
.avz__stat { padding:14px 15px; border-right:1px solid var(--avz-line); }
.avz__stat:last-child { border-right:0; }
.avz__stat b { display:block; font-size:1.28rem; font-weight:800; letter-spacing:-.03em; color:var(--avz-ink); }
.avz__stat.in b { color:var(--avz-acc); }
.avz__stat.out b { color:var(--avz-out); }
.avz__stat span { font-size:.74rem; font-weight:600; color:var(--avz-mut); }

.avz__tog { display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:9px 15px; border-bottom:1px solid var(--avz-line); font-size:13px; color:var(--avz-ink); text-transform:capitalize; }
.avz__tog:last-child { border-bottom:0; }
.avz__pill { font-size:10px; font-weight:800; letter-spacing:.06em; text-transform:uppercase;
  padding:3px 9px; border-radius:999px; white-space:nowrap; text-transform:none;
  background:rgba(255,255,255,.07); color:var(--avz-mut); }
.avz__pill.on { background:rgba(48,209,88,.14); color:var(--avz-on); text-transform:uppercase; }

.avz__bar { padding:9px 15px; }
.avz__bar-top { display:flex; justify-content:space-between; font-size:12.5px; color:var(--avz-ink); margin-bottom:5px; }
.avz__bar-top em { font-style:normal; color:var(--avz-mut); font-size:11.5px; }
.avz__bar-track { height:8px; border-radius:99px; background:var(--avz-line); overflow:hidden; }
.avz__bar-fill { height:100%; border-radius:99px; background:var(--avz-acc); transition:width .5s ease; }

.avz__step { display:grid; grid-template-columns:88px 1fr; gap:12px; padding:10px 15px;
  border-bottom:1px solid var(--avz-line); font-size:13px; color:var(--avz-ink); }
.avz__step:last-child { border-bottom:0; }
.avz__step b { color:var(--avz-acc); font-weight:800; font-size:11.5px; letter-spacing:.04em; text-transform:uppercase; }
.avz__note { padding:0 15px 10px; font-size:12px; color:var(--avz-mut); }
`;

export default function AriaVisual({ spec, theme }) {
  if (!spec) return null;
  const cls = 'avz' + (theme === 'day' ? ' is-light' : '');

  const body = () => {
    if (spec.kind === 'table') {
      if (!spec.rows || !spec.rows.length) return <div className="avz__empty">{spec.footer || 'Nothing here.'}</div>;
      return (
        <table>
          <thead><tr>{spec.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{spec.rows.map((r, i) => <tr key={i}>{r.map((c, k) => <td key={k}>{c}</td>)}</tr>)}</tbody>
        </table>
      );
    }
    if (spec.kind === 'stats') {
      return <div className="avz__stats">{spec.stats.map((s, i) => (
        <div className={'avz__stat' + (s.tone ? ' ' + s.tone : '')} key={i}><b>{s.value}</b><span>{s.label}</span></div>
      ))}</div>;
    }
    if (spec.kind === 'toggles') {
      if (!spec.rows || !spec.rows.length) return <div className="avz__empty">Nothing wired yet.</div>;
      return <>{spec.rows.map(([name, state], i) => (
        <div className="avz__tog" key={i}>
          <span>{name}</span>
          <span className={'avz__pill' + (state === 'on' ? ' on' : '')}>{state}</span>
        </div>
      ))}</>;
    }
    if (spec.kind === 'bars') {
      const max = Math.max(...spec.bars.map((b) => b.value)) || 1;
      return <>{spec.note && <div className="avz__note">{spec.note}</div>}
        {spec.bars.map((b, i) => (
          <div className="avz__bar" key={i}>
            <div className="avz__bar-top"><span>{b.label}</span>{b.caption && <em>{b.caption}</em>}</div>
            <div className="avz__bar-track"><div className="avz__bar-fill" style={{ width: (b.value / max * 100) + '%' }} /></div>
          </div>
        ))}</>;
    }
    if (spec.kind === 'steps') {
      return <>{spec.steps.map((s, i) => (
        <div className="avz__step" key={i}><b>{s.when}</b><span>{s.what}</span></div>
      ))}</>;
    }
    return null;
  };

  return (
    <div className={cls}>
      <style>{S}</style>
      {spec.title && <div className="avz__head">{spec.title}</div>}
      {body()}
      {spec.footer && spec.kind !== 'table' && <div className="avz__foot">{spec.footer}</div>}
      {spec.footer && spec.kind === 'table' && spec.rows && spec.rows.length > 0 && <div className="avz__foot">{spec.footer}</div>}
    </div>
  );
}
